import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Star, MapPin, ShieldCheck, Truck, Building2, FileText, BadgeCheck, Lock } from "lucide-react";
import { TeamBadge } from "@/components/TeamBadge";
import { fileUrl } from "@/lib/files";
import { labelFor } from "@/lib/services";

const PrivateProfile = () => (
  <div className="container-x py-20 max-w-md" data-testid="collector-profile-private">
    <div className="card-soft text-center">
      <Lock className="w-10 h-10 mx-auto text-[#8A9A86]"/>
      <h1 className="font-heading text-2xl text-[#1E3F33] mt-4">Collector profiles are private</h1>
      <p className="text-sm text-[#5C6B64] mt-2">Submit a request and nearby certified collectors will contact you directly. You'll see who they are once they reach out.</p>
      <Link to="/search" className="btn-primary inline-block mt-6" data-testid="collector-profile-private-cta">Request a collector</Link>
    </div>
  </div>
);

export default function CollectorProfile() {
  const { id } = useParams();
  const [collector, setCollector] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [blocked, setBlocked] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get(`/collectors/${id}`),
        api.get(`/reviews/collector/${id}`),
      ]);
      setCollector(c);
      setReviews(r);
    } catch {
      setBlocked(true);
    }
  };

  useEffect(() => { load(); }, [id]);

  if (blocked) return <PrivateProfile />;
  if (!collector) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;

  return (
    <div className="container-x py-12" data-testid="collector-profile-page">
      <div className="grid lg:grid-cols-12 gap-10">
        {/* Left */}
        <aside className="lg:col-span-4 space-y-6">
          <div className="card-soft">
            <div className="w-full h-48 rounded-xl overflow-hidden bg-[#F0EFEA] mb-5">
              {collector.photo_url ? (
                <img src={collector.photo_url.startsWith("http") ? collector.photo_url : `${API}/files/${collector.photo_url}`} alt={collector.business_name} className="w-full h-full object-cover" data-testid="collector-profile-photo" />
              ) : collector.company_logo_url ? (
                <img src={fileUrl(collector.company_logo_url)} alt={`${collector.company_name} logo`} className="w-full h-full object-contain p-6 bg-white" data-testid="collector-profile-photo" />
              ) : (
                <div className="w-full h-full grid place-items-center text-[#8A9A86]"><Building2 className="w-12 h-12" strokeWidth={1.4}/></div>
              )}
            </div>
            <h1 className="font-heading text-2xl text-[#1E3F33] font-semibold" data-testid="collector-business-name">{collector.business_name}</h1>
            {collector.company_name && <div><TeamBadge large name={collector.company_name} logo={collector.company_logo_url} testid="collector-company-name" /></div>}
            <div className="flex items-center gap-1 mt-1 text-sm">
              <Star className="w-4 h-4 fill-[#CC5A3A] text-[#CC5A3A]" />
              <span className="font-medium text-[#1E3F33]">{collector.rating_avg?.toFixed?.(1) || "—"}</span>
              <span className="text-[#8A9A86]">({collector.rating_count} reviews)</span>
            </div>
            {collector.rating_count > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs font-medium px-3 py-1" data-testid="verified-reviews-badge">
                <BadgeCheck className="w-3.5 h-3.5" /> {collector.rating_count} verified review{collector.rating_count === 1 ? "" : "s"}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-3">
              {collector.subscription_active && (<span className="badge-verified"><ShieldCheck className="w-3 h-3"/> Certified Pro</span>)}
              {(collector.service_type === "mobile" || collector.service_type === "both") && (<span className="badge-mobile"><Truck className="w-3 h-3"/> Mobile</span>)}
              {(collector.service_type === "stationary" || collector.service_type === "both") && (<span className="badge-stationary"><Building2 className="w-3 h-3"/> Brick &amp; Mortar</span>)}
            </div>
            {(collector.city || collector.address) && (
              <div className="text-sm text-[#5C6B64] mt-4 flex items-center gap-1"><MapPin className="w-4 h-4"/> {[collector.city, collector.state].filter(Boolean).join(", ")}</div>
            )}
            <div className="mt-4 rounded-xl border border-[#1E3F33]/10 bg-[#F0EFEA]/50 p-3 text-xs text-[#5C6B64]" data-testid="contact-hidden-note">
              To get in touch, complete the <Link to="/search" className="text-[#CC5A3A] font-medium hover:underline">Find a collector</Link> process and a collector will reach out to you directly.
            </div>
            {collector.hourly_rate ? (
              <div className="text-sm text-[#1E3F33] mt-3 font-medium">Starting at ${collector.hourly_rate}/hr</div>
            ) : null}
          </div>

          {collector.certifications?.length > 0 && (
            <div className="card-soft" data-testid="collector-certifications">
              <h3 className="font-heading text-lg text-[#1E3F33] mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#2D6A4F]"/> Certifications</h3>
              <ul className="space-y-2">
                {collector.certifications.map((c, i) => (
                  <li key={i}>
                    <a
                      href={`${API}/files/${c.file_path}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`cert-link-${i}`}
                      className="text-sm text-[#CC5A3A] hover:underline inline-flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4"/> {c.original_name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        {/* Right */}
        <main className="lg:col-span-8 space-y-6">
          <div className="card-soft">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-2">About</h2>
            <p className="text-[#5C6B64]">{collector.bio || "No bio provided."}</p>

            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div>
                <h3 className="text-sm uppercase tracking-wider text-[#8A9A86] mb-2">Services</h3>
                <div className="flex flex-wrap gap-2">
                  {collector.services?.length ? collector.services.map((s)=>{
                    const sup = collector.service_supplies?.[s];
                    return (
                      <span key={s} className="text-xs bg-[#F0EFEA] text-[#1E3F33] rounded-full px-3 py-1 inline-flex items-center gap-1.5">
                        {labelFor(s)}
                        {sup === true && <span className="text-[10px] bg-[#2D6A4F] text-white rounded-full px-1.5 py-0.5">supplies ✓</span>}
                        {sup === false && <span className="text-[10px] bg-[#E63946]/15 text-[#E63946] rounded-full px-1.5 py-0.5">no supplies</span>}
                      </span>
                    );
                  }) : <span className="text-sm text-[#8A9A86]">None listed</span>}
                </div>
              </div>
              <div>
                <h3 className="text-sm uppercase tracking-wider text-[#8A9A86] mb-2">Supplies</h3>
                <div className="flex flex-wrap gap-2">
                  {collector.supplies?.length ? collector.supplies.map((s)=>(
                    <span key={s} className="text-xs bg-[#F0EFEA] text-[#1E3F33] rounded-full px-3 py-1">{s}</span>
                  )) : <span className="text-sm text-[#8A9A86]">None listed</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Booking form removed — customers now broadcast a service request from /search */}

          {/* Reviews */}
          <div className="card-soft" data-testid="reviews-section">
            <h2 className="font-heading text-xl text-[#1E3F33] mb-4">Reviews</h2>
            <div className="text-xs text-[#5C6B64] bg-[#F0EFEA]/50 border border-[#1E3F33]/10 rounded-xl p-3 mb-6" data-testid="review-gate-note">
              Reviews can only be left after you've completed the <Link to="/search" className="text-[#CC5A3A] font-medium hover:underline">Find a collector</Link> process and confirmed this collector contacted you — from your request tracking link.
            </div>
            {reviews.length === 0 ? (
              <div className="text-[#8A9A86] text-sm">No reviews yet.</div>
            ) : (
              <ul className="space-y-5">
                {reviews.map((r)=>(
                  <li key={r.id} data-testid={`review-${r.id}`}>
                    <div className="flex items-center gap-2">
                      {[1,2,3,4,5].map((n)=>(
                        <Star key={n} className={`w-4 h-4 ${n<=r.rating?"fill-[#CC5A3A] text-[#CC5A3A]":"text-[#8A9A86]"}`}/>
                      ))}
                      <span className="text-sm font-medium text-[#1E3F33] ml-2">{r.customer_name}</span>
                    </div>
                    <p className="text-[#5C6B64] mt-1.5">{r.comment}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
